import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Form, Spinner, Button } from "react-bootstrap";
import {
  FiBarChart2,
  FiUsers,
  FiSearch,
  FiAlertTriangle,
  FiArrowLeft, // <-- 1. Import the back arrow icon
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import UserCard from "./UserCard"; // Assuming you have this component
import "./PerformanceStatistics.css";

// Recursive helper to find and update a user anywhere in the nested tree
const findAndModifyUser = (users, userId, modification) => {
  return users.map((user) => {
    if (user.user_id === userId) {
      return { ...user, ...modification(user) };
    }
    if (user.subordinates) {
      return {
        ...user,
        subordinates: findAndModifyUser(
          user.subordinates,
          userId,
          modification
        ),
      };
    }
    return user;
  });
};

const PerformanceStatistics = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    location: "all",
    role: "all",
    searchTerm: "",
  });

  const roles = [
    { value: "all", label: "All Roles" },
    { value: "director", label: "Directors" },
    { value: "zonal_head", label: "Zonal Heads" },
    { value: "branch_manager", label: "Branch Managers" },
    { value: "team_lead", label: "Team Leads" },
    { value: "relationship_manager", label: "Relationship Managers" },
  ];

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        location: filters.location === "all" ? undefined : filters.location,
        role: filters.role === "all" ? undefined : filters.role,
      };
      const response = await api.get("/api/admin/performance-statistics", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        params,
      });
      setUsers(
        Array.isArray(response.data)
          ? response.data.map((u) => ({
              ...u,
              isExpanded: false,
              isLoading: false,
            }))
          : []
      );
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Could not load user data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters.location, filters.role]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get(
          "/api/admin/performance-statistics/locations",
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );
        setLocations(["all", ...response.data]);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleSubordinates = useCallback(async (userId) => {
    // ... (This function remains unchanged)
    let userToToggle;
    const findUser = (nodes) => {
      if (!nodes) return null;
      for (const node of nodes) {
        if (node.user_id === userId) return node;
        if (node.subordinates) {
          const found = findUser(node.subordinates);
          if (found) return found;
        }
      }
      return null;
    };
    setUsers((currentUsers) => {
      userToToggle = findUser(currentUsers);
      if (!userToToggle) return currentUsers;
      const isExpanding = !userToToggle.isExpanded;
      let newUsers = findAndModifyUser(currentUsers, userId, () => ({
        isExpanded: isExpanding,
      }));
      if (isExpanding && !userToToggle.subordinates) {
        newUsers = findAndModifyUser(newUsers, userId, () => ({
          isLoading: true,
        }));
        api
          .get(`/api/admin/performance-statistics/subordinates/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          })
          .then((response) => {
            const subsWithState = Array.isArray(response.data)
              ? response.data.map((u) => ({
                  ...u,
                  isExpanded: false,
                  isLoading: false,
                }))
              : [];
            setUsers((prev) =>
              findAndModifyUser(prev, userId, () => ({
                subordinates: subsWithState,
                isLoading: false,
              }))
            );
          })
          .catch((error) => {
            console.error(`Error fetching subordinates for ${userId}:`, error);
            setUsers((prev) =>
              findAndModifyUser(prev, userId, () => ({
                subordinates: [],
                isLoading: false,
              }))
            );
          });
      }
      return newUsers;
    });
  }, []);

  const getRoleDisplayName = (role, single = false) => {
    const roleMap = {
      director: "Director",
      zonal_head: "Zonal Head",
      branch_manager: "Branch Manager",
      team_lead: "Team Lead",
      relationship_manager: "Relationship Manager",
    };
    return single ? roleMap[role] || role : `${roleMap[role] || role}s`;
  };

  const displayedUsers = Array.isArray(users)
    ? users.filter(
        (user) =>
          (user.name?.toLowerCase() || "").includes(
            filters.searchTerm.toLowerCase()
          ) ||
          (user.username?.toLowerCase() || "").includes(
            filters.searchTerm.toLowerCase()
          )
      )
    : [];

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="dashboard-container">
      <aside className="navigator-panel">
        <header className="navigator-header">
          <FiUsers /> Performance
        </header>
        <ul className="role-filter-list">
          {roles.map((role) => (
            <li key={role.value} className="role-filter-item">
              <button
                className={filters.role === role.value ? "active" : ""}
                onClick={() => handleFilterChange("role", role.value)}
              >
                {role.label}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <main className="content-panel">
        {/* ======================================================= */}
        {/* ============== HEADER UPDATED HERE ============== */}
        {/* ======================================================= */}
        <div className="content-header d-flex justify-content-between align-items-center mb-4">
          <h2 className="page-title mb-0">
            <FiBarChart2 className="me-2" />
            Statistics Dashboard
          </h2>
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/admin-dashboard")}
            >
              <FiArrowLeft className="me-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
        {/* ======================================================= */}

        <div className="filter-bar">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FiSearch className="me-2" /> Search Users
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by Name or Username..."
                  value={filters.searchTerm}
                  onChange={(e) =>
                    handleFilterChange("searchTerm", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Location</Form.Label>
                <Form.Select
                  value={filters.location}
                  onChange={(e) =>
                    handleFilterChange("location", e.target.value)
                  }
                >
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc === "all" ? "All Locations" : loc}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {loading && (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="text-center p-5 text-danger">
            <FiAlertTriangle className="mb-2" size={24} />
            <p>{error}</p>
            <Button variant="primary" size="sm" onClick={fetchUsers}>
              Retry
            </Button>
          </div>
        )}

        {!loading &&
          !error &&
          (displayedUsers.length === 0 ? (
            <div className="text-center p-5 text-secondary">
              <h4>No Users Found</h4>
              <p>Your filters did not match any users.</p>
            </div>
          ) : (
            <div>
              {/* Assuming UserCard is a defined component */}
              {displayedUsers.map((user) => (
                <UserCard
                  key={user.user_id}
                  user={user}
                  onToggle={toggleSubordinates}
                  onReport={(userId) =>
                    navigate(`/admin-monthly-report/${userId}`)
                  }
                  getRoleDisplayName={getRoleDisplayName}
                />
              ))}
            </div>
          ))}
      </main>
    </div>
  );
};

export default PerformanceStatistics;
